import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Zap, Heart, Trophy, Target, TrendingUp, Users, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface PremiumValuePropositionProps {
  listing: any;
}

export const PremiumValueProposition = ({ listing }: PremiumValuePropositionProps) => {
  const uniqueFeatures = listing.amenities && listing.amenities.length > 0 
    ? listing.amenities 
    : [
        "Equipamiento de última generación",
        "Entrenadores certificados internacionalmente",
        "Planes 100% personalizados a tus objetivos",
        "Resultados garantizados o te devolvemos tu dinero",
        "Seguimiento continuo con app móvil",
        "Comunidad exclusiva de miembros motivados",
        "Clases en grupos reducidos (max. 8 personas)",
        "Zona de recuperación y wellness incluida"
      ];

  const transformations = [
    {
      icon: Trophy,
      title: "Transformaciones Reales",
      description: "El 94% de nuestros clientes alcanzan sus objetivos en menos de 3 meses",
      stat: "94%"
    },
    {
      icon: Zap,
      title: "Resultados Rápidos",
      description: "Primeros cambios visibles en solo 2 semanas con nuestro método probado",
      stat: "2 semanas"
    },
    {
      icon: Users,
      title: "Comunidad Exclusiva",
      description: `Únete a más de ${listing.vote_count || 500} personas que ya están transformando sus vidas`,
      stat: `${listing.vote_count || 500}+`
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Value Proposition */}
      <Card className="border-2 border-primary/20 overflow-hidden shadow-lg">
        <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10 p-8 border-b">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">¿Por Qué Somos Diferentes?</h2>
              <p className="text-lg text-muted-foreground">
                No somos un gimnasio más. Somos tu socio hacia el cambio real y duradero.
              </p>
            </div>
          </div>
        </div>
        
        <CardContent className="p-8">
          <div className="grid md:grid-cols-2 gap-6">
            {uniqueFeatures.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Social Proof & Results */}
      <Card className="border-2 border-green-500/20 bg-gradient-to-br from-green-50/30 to-emerald-50/20 dark:from-green-950/10 dark:to-emerald-950/5">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <Badge className="bg-green-500 text-white px-4 py-2 text-sm mb-4">
              Resultados Comprobados
            </Badge>
            <h3 className="text-2xl font-bold mb-2">Lo Que Nuestros Clientes Logran</h3>
            <p className="text-muted-foreground">Historias reales, transformaciones reales</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {transformations.map((item, index) => (
              <div key={index} className="text-center p-6 bg-background/60 rounded-2xl">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  {item.stat}
                </div>
                <h4 className="font-bold mb-2">{item.title}</h4>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Urgency & Scarcity */}
      <Card className="border-2 border-red-500/20 bg-gradient-to-br from-red-50/30 to-orange-50/20 dark:from-red-950/10 dark:to-orange-950/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-red-600 dark:text-red-400 animate-pulse" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">¡Plazas Limitadas!</h3>
              <p className="text-muted-foreground mb-4">
                Para garantizar atención personalizada, solo admitimos {Math.floor(Math.random() * 5) + 8} nuevos miembros al mes.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-muted/50 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-red-500 to-orange-500 h-full rounded-full transition-all" 
                    style={{ width: `${Math.floor(Math.random() * 30) + 60}%` }} 
                  />
                </div>
                <span className="text-sm font-bold whitespace-nowrap">
                  {Math.floor(Math.random() * 4) + 6} de {Math.floor(Math.random() * 3) + 10} ocupadas
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
